import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { OptionSymbol } from '@/components/ui/OptionSymbol';
import { useRollPosition } from '@/hooks/queries/usePositions';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { PositionWithLegs, CreateLegRequest, CloseLegRequest } from '@steward/shared';

interface RollPositionFormProps {
  position: PositionWithLegs;
  chainId: string;
  underlyingSymbol: string;
}

interface FormValues {
  closingLegs: Array<{
    legId: string;
    quantity: number;
    pricePerContract: number;
  }>;
  newLegs: Array<{
    underlyingSymbol: string;
    optionType: 'call' | 'put';
    strikePrice: number;
    expirationDate: string;
    action: 'BTO' | 'STO';
    quantity: number;
    pricePerContract: number;
  }>;
  notes: string;
}

const actionOptions = [
  { value: 'STO', label: 'Sell to Open' },
  { value: 'BTO', label: 'Buy to Open' },
];

const typeOptions = [
  { value: 'call', label: 'Call' },
  { value: 'put', label: 'Put' },
];

export function RollPositionForm({ position, chainId, underlyingSymbol }: RollPositionFormProps) {
  const navigate = useNavigate();
  const rollPosition = useRollPosition();

  const openLegs = position.legs.filter((leg) => leg.quantityRemaining > 0);

  const { register, control, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      closingLegs: openLegs.map((leg) => ({
        legId: leg.id,
        quantity: leg.quantityRemaining,
        pricePerContract: 0,
      })),
      newLegs: openLegs.map((leg) => ({
        underlyingSymbol: leg.underlyingSymbol,
        optionType: leg.optionType,
        strikePrice: leg.strikePrice,
        expirationDate: '',
        action: leg.action === 'STO' || leg.action === 'STC' ? 'STO' : 'BTO',
        quantity: leg.quantityRemaining,
        pricePerContract: 0,
      })),
      notes: '',
    },
  });

  const { fields: newLegFields, append, remove } = useFieldArray({
    control,
    name: 'newLegs',
  });

  const watchedClosingLegs = watch('closingLegs');
  const watchedNewLegs = watch('newLegs');

  const calculateClosingPremium = () => {
    return watchedClosingLegs.reduce((sum, watchedLeg, index) => {
      const originalLeg = openLegs[index];
      const value = watchedLeg.pricePerContract * watchedLeg.quantity * 100;
      const isClosingSell = originalLeg.action === 'BTO' || originalLeg.action === 'BTC';
      return sum + (isClosingSell ? value : -value);
    }, 0);
  };

  const calculateNewPremium = () => {
    return watchedNewLegs.reduce((sum, leg) => {
      const value = leg.pricePerContract * leg.quantity * 100;
      return sum + (leg.action === 'STO' ? value : -value);
    }, 0);
  };

  const closingPremium = calculateClosingPremium();
  const newPremium = calculateNewPremium();
  const netCredit = closingPremium + newPremium;

  const onSubmit = async (data: FormValues) => {
    const closingLegs: CloseLegRequest[] = data.closingLegs
      .filter((leg) => leg.quantity > 0 && leg.pricePerContract > 0)
      .map((leg) => ({
        legId: leg.legId,
        quantity: leg.quantity,
        pricePerContract: leg.pricePerContract,
      }));

    const newLegs: CreateLegRequest[] = data.newLegs
      .filter((leg) => leg.quantity > 0 && leg.pricePerContract > 0 && leg.expirationDate)
      .map((leg) => ({
        underlyingSymbol: leg.underlyingSymbol,
        optionType: leg.optionType,
        strikePrice: leg.strikePrice,
        expirationDate: leg.expirationDate,
        action: leg.action,
        quantity: leg.quantity,
        pricePerContract: leg.pricePerContract,
      }));

    if (closingLegs.length === 0 || newLegs.length === 0) return;

    await rollPosition.mutateAsync({
      id: position.id,
      data: {
        closingLegs,
        newLegs,
        notes: data.notes || undefined,
      },
    });

    navigate(`/chains/${chainId}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Close existing legs */}
      <Card>
        <CardHeader>
          <CardTitle>Close Existing Position</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {openLegs.map((leg, index) => {
            const closingAction =
              leg.action === 'STO' ? 'BTC' :
              leg.action === 'BTO' ? 'STC' :
              leg.action === 'STC' ? 'BTO' : 'STO';

            return (
              <div key={leg.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-500">{closingAction}</span>
                    <OptionSymbol symbol={leg.optionSymbol} compact />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Quantity"
                    type="number"
                    min={1}
                    max={leg.quantityRemaining}
                    {...register(`closingLegs.${index}.quantity`, { valueAsNumber: true })}
                  />
                  <Input
                    label="Price"
                    type="number"
                    step="0.01"
                    min={0}
                    {...register(`closingLegs.${index}.pricePerContract`, { valueAsNumber: true })}
                  />
                </div>
              </div>
            );
          })}
          <div className="text-right text-sm">
            Closing credit/debit:{' '}
            <span className={cn('font-medium', closingPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(closingPremium)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Open new legs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Open New Position</CardTitle>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              append({
                underlyingSymbol,
                optionType: 'put',
                strikePrice: 0,
                expirationDate: '',
                action: 'STO',
                quantity: 1,
                pricePerContract: 0,
              })
            }
          >
            Add Leg
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {newLegFields.map((field, index) => (
            <div key={field.id} className="p-4 border border-gray-200 rounded-lg space-y-4">
              <div className="flex justify-between items-start">
                <h4 className="font-medium text-sm text-gray-700">Leg {index + 1}</h4>
                {newLegFields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Select
                  label="Action"
                  options={actionOptions}
                  {...register(`newLegs.${index}.action`)}
                />
                <Select
                  label="Type"
                  options={typeOptions}
                  {...register(`newLegs.${index}.optionType`)}
                />
                <Input
                  label="Strike"
                  type="number"
                  step="0.5"
                  {...register(`newLegs.${index}.strikePrice`, { valueAsNumber: true })}
                />
                <Input
                  label="Expiration"
                  type="date"
                  {...register(`newLegs.${index}.expirationDate`)}
                />
                <Input
                  label="Quantity"
                  type="number"
                  min={1}
                  {...register(`newLegs.${index}.quantity`, { valueAsNumber: true })}
                />
                <Input
                  label="Price"
                  type="number"
                  step="0.01"
                  {...register(`newLegs.${index}.pricePerContract`, { valueAsNumber: true })}
                />
              </div>
            </div>
          ))}
          <div className="text-right text-sm">
            New position credit/debit:{' '}
            <span className={cn('font-medium', newPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(newPremium)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900">Net Roll Credit/Debit</span>
            <span className={cn('text-xl font-bold', netCredit >= 0 ? 'text-green-600' : 'text-red-600')}>
              {formatCurrency(netCredit)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Notes & Actions */}
      <Card>
        <CardContent className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              {...register('notes')}
              rows={2}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Add notes about this roll..."
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => navigate(`/chains/${chainId}`)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={rollPosition.isPending}>
              Roll Position
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
